import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { searchShortcodes, type EmojiEntry } from '@/lib/emojiShortcodes'

interface UseEmojiShortcodeOpts {
  value: string
  onChange: (next: string) => void
  textareaRef: React.RefObject<HTMLTextAreaElement | null>
}

interface ShortcodeState {
  active: boolean
  query: string
  startIndex: number  // position of the `:` that opened the popover
  matches: EmojiEntry[]
  selectedIndex: number
}

const empty: ShortcodeState = { active: false, query: '', startIndex: -1, matches: [], selectedIndex: 0 }

/**
 * Slack/GitHub-style `:` emoji autocomplete for a textarea.
 *
 * - Opens when user types `:` (at the start, or after whitespace).
 * - Filters as they type more chars.
 * - Closes on space, Escape, or selection.
 * - Arrow keys navigate; Enter/Tab insert.
 */
export function useEmojiShortcode({ value, onChange, textareaRef }: UseEmojiShortcodeOpts) {
  const [state, setState] = useState<ShortcodeState>(empty)
  const valueRef = useRef(value)
  valueRef.current = value

  // Re-derive popover state from the textarea selection on every value change.
  useEffect(() => {
    const ta = textareaRef.current
    if (!ta) return setState(empty)

    const caret = ta.selectionStart ?? value.length
    // Find the closest `:` before the caret that's a valid trigger
    let i = caret - 1
    while (i >= 0) {
      const ch = value[i]
      if (ch === ':') break
      if (ch === ' ' || ch === '\n' || ch === '\t') return setState(empty)
      i--
    }
    if (i < 0) return setState(empty)
    // Only open if `:` is at start or preceded by whitespace
    const prev = value[i - 1]
    if (prev !== undefined && prev !== ' ' && prev !== '\n' && prev !== '\t') return setState(empty)

    const query = value.slice(i + 1, caret)
    // Must have at least 0 chars and only word chars
    if (!/^\w*$/.test(query)) return setState(empty)

    const matches = searchShortcodes(query, 6)
    setState(prev => ({
      active: matches.length > 0,
      query,
      startIndex: i,
      matches,
      selectedIndex: prev.startIndex === i ? Math.min(prev.selectedIndex, Math.max(matches.length - 1, 0)) : 0
    }))
  }, [value, textareaRef])

  const close = useCallback(() => setState(empty), [])

  const insert = useCallback((entry: EmojiEntry) => {
    const ta = textareaRef.current
    if (!ta || state.startIndex < 0) return

    const caret = ta.selectionStart ?? valueRef.current.length
    const before = valueRef.current.slice(0, state.startIndex)
    const after = valueRef.current.slice(caret)
    const next = before + entry.emoji + after
    onChange(next)

    // Restore caret after the inserted emoji
    requestAnimationFrame(() => {
      const pos = before.length + entry.emoji.length
      ta.focus()
      ta.setSelectionRange(pos, pos)
    })
    setState(empty)
  }, [onChange, state.startIndex, textareaRef])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!state.active) return false
    if (e.key === 'Escape') { e.preventDefault(); close(); return true }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setState(s => ({ ...s, selectedIndex: (s.selectedIndex + 1) % s.matches.length }))
      return true
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setState(s => ({ ...s, selectedIndex: (s.selectedIndex - 1 + s.matches.length) % s.matches.length }))
      return true
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault()
      const entry = state.matches[state.selectedIndex]
      if (entry) insert(entry)
      return true
    }
    return false
  }, [state, close, insert])

  return useMemo(() => ({
    active: state.active,
    query: state.query,
    matches: state.matches,
    selectedIndex: state.selectedIndex,
    select: (i: number) => setState(s => ({ ...s, selectedIndex: i })),
    insert,
    close,
    handleKeyDown
  }), [state, insert, close, handleKeyDown])
}
